import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
} from "@mui/material";

const DURATIONS = [
  { value: "DAYS_1", label: "1 day" },
  { value: "DAYS_3", label: "3 days" },
  { value: "DAYS_5", label: "5 days" },
  { value: "DAYS_7", label: "7 days" },
  { value: "DAYS_10", label: "10 days" },
];

export default function ConvertToAuctionModal({ open, listing, onClose, onConfirm }) {
  const defaultDuration = useMemo(() => {
    const v = (listing?.listing_duration || "").toUpperCase();
    // For conversion, default to 3 days if GTC/blank
    if (!v || v === "GTC") return "DAYS_3";
    if (DURATIONS.some((d) => d.value === v)) return v;
    return "DAYS_3";
  }, [listing]);

  const [duration, setDuration] = useState(defaultDuration);
  const [startPrice, setStartPrice] = useState(listing?.listing_price ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  React.useEffect(() => {
    if (open) {
      setDuration(defaultDuration);
      setStartPrice(listing?.listing_price ?? "");
      setBusy(false);
      setErr("");
    }
  }, [open, defaultDuration, listing]);

  const handleConfirm = async () => {
    setErr("");
    const sp = Number(startPrice);
    if (!duration) {
      setErr("Auction duration is required.");
      return;
    }
    if (!Number.isFinite(sp) || sp <= 0) {
      setErr("Start price must be a positive number.");
      return;
    }

    setBusy(true);
    try {
      await onConfirm({ listing_duration: duration, start_price: sp });
      onClose();
    } catch (e) {
      setErr(e?.message || "Failed to convert to auction");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Convert to Auction</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This will end the current Buy It Now listing and create a new Auction listing draft.
          Publishing the new auction is out of scope for this version.
        </Alert>

        {err ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {err}
          </Alert>
        ) : null}

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="auction-duration-label">Auction Duration</InputLabel>
          <Select
            labelId="auction-duration-label"
            label="Auction Duration"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            disabled={busy}
          >
            {DURATIONS.map((d) => (
              <MenuItem key={d.value} value={d.value}>
                {d.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Start Price"
          type="number"
          inputProps={{ step: "0.01", min: "0" }}
          value={startPrice}
          onChange={(e) => setStartPrice(e.target.value)}
          disabled={busy}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={busy}>
          Convert
        </Button>
      </DialogActions>
    </Dialog>
  );
}
